import Presets, { type PresetsType } from '@assets/hdri';
import { type Measurement } from '@components/Viewer/Measurement/Measurement';
import { KEY } from '@constants/key';
import { MEASUREMENT_COLOR, type MeasurementColor } from '@constants/measurements';
import type SPInspection from '@lib/spproject/SPInspection';
import type SPProject from '@lib/spproject/SPProject';
import { type Inspection } from '@lib/spproject/types';
import { createSlice, type Dispatch, type PayloadAction } from '@reduxjs/toolkit';

export const defaultRoomEnv = 'warehouse';

interface ViewerState {
  project: SPProject | null;
  inspection: SPInspection | null;
  selectedPOI: Inspection.PointOfInterest | null;
  stepIndex: number;
  isLoading: boolean;
  roomEnvironment: PresetsType;
  isPerspective: boolean;
  isRefreshed: boolean;
  isMeasuring: boolean;
  isMeasurementColorModalOpen: boolean;
  measurementColor: MeasurementColor;
  measurementLines: Measurement[];
  isInspectionDetailModalOpen: boolean;
}

const initialState: ViewerState = {
  project: null,
  inspection: null,
  selectedPOI: null,
  stepIndex: 0,
  isLoading: false,
  roomEnvironment: localStorage.getItem(KEY.ROOM_ENV) as PresetsType | null ?? defaultRoomEnv,
  isPerspective: true,
  isRefreshed: false,
  isMeasuring: false,
  isMeasurementColorModalOpen: false,
  measurementColor: MEASUREMENT_COLOR.RED,
  measurementLines: [],
  isInspectionDetailModalOpen: false,
};

const viewerSlice = createSlice({
  name: 'viewer',
  initialState,
  reducers: {
    loadProject(
      state,
      action: PayloadAction<{ project: SPProject; inspection?: SPInspection; }>
    ) {
      state.project = action.payload.project;
      state.isLoading = false;

      if (action.payload.inspection) {
        state.inspection = action.payload.inspection;
        state.selectedPOI = null;
        state.stepIndex = 0;
        state.isInspectionDetailModalOpen = false;
      }
    },

    setInspection(state, action: PayloadAction<SPInspection | null>) {
      state.inspection = action.payload;
      state.selectedPOI = null;
      state.stepIndex = 0;
      state.isInspectionDetailModalOpen = false;
    },

    setSelectedPOI(state, action: PayloadAction<Inspection.PointOfInterest | null>) {
      state.selectedPOI = action.payload;
      state.stepIndex = 0;
      state.isInspectionDetailModalOpen = Boolean(action.payload);
    },

    setStepIndex(state, action: PayloadAction<number>) {
      state.stepIndex = action.payload;
    },

    setRefresh(state, action: PayloadAction<boolean>) {
      state.isRefreshed = action.payload;
    },

    setPerspective(state, action: PayloadAction<boolean>) {
      state.isPerspective = action.payload;
    },

    setMeasuringState(state, action: PayloadAction<boolean>) {
      state.isMeasuring = action.payload;
    },

    setMeasurementColorModalState(state, action: PayloadAction<boolean>) {
      state.isMeasurementColorModalOpen = action.payload;
    },

    setMeasurementColor(state, action: PayloadAction<MeasurementColor>) {
      state.measurementColor = action.payload;
    },

    setMeasurementLines(state, action: PayloadAction<Measurement[]>) {
      state.measurementLines = action.payload;
    },

    setRoomEnvironment(state, action: PayloadAction<PresetsType>) {
      state.roomEnvironment = action.payload;
    },

    reset(state) {
      Object.assign(state, {
        ...initialState,
        roomEnvironment: localStorage.getItem(KEY.ROOM_ENV) as PresetsType | null ?? defaultRoomEnv
      });
    },
  },
});

export const {
  loadProject,
  setStepIndex,
  setInspection,
  reset,
  setRefresh,
  setPerspective,
  setMeasuringState,
  setMeasurementColorModalState,
  setMeasurementColor,
  setMeasurementLines,
  setSelectedPOI,
  setRoomEnvironment,
} = viewerSlice.actions;

/**
 * Picks the viewer's backdrop and remembers it in this browser, which the initial state
 * and `reset` read back — so the two can't be set apart. A name that is no longer a preset
 * is ignored: the account keeps its own copy of the list, so one dropped here could still
 * come back from the server.
 */
export const chooseRoomEnvironment = (environment: string) => (dispatch: Dispatch) => {
  if (!Object.keys(Presets).includes(environment)) return;

  localStorage.setItem(KEY.ROOM_ENV, environment);
  dispatch(setRoomEnvironment(environment as PresetsType));
};

export default viewerSlice.reducer;
